import { MutedAlert } from '@app/components/MutedAlert';
import { SocialExportNoPlatformsAlert } from '@app/modules/fragments/components/social-export/SocialExportNoPlatformsAlert';
import { SocialExportNoSourcesAlert } from '@app/modules/fragments/components/social-export/SocialExportNoSourcesAlert';
import { useAvailableSocialExportSources } from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { useFragment } from '@app/modules/fragments/screens/view/hooks/useFragment';
import { webApi } from '@mi-front/api-clients';
import { QueryView } from '@mi-front/ui';
import { Stack } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';
import { SocialExportUploadItem } from './SocialExportUploadItem';
import { useAvailableExportPlatforms } from './hooks/useAvailableExportPlatforms';
import { useFragmentSocialUploadsQuery } from './hooks/useFragmentSocialUploadsQuery';

interface SocialExportPanoramaProps {
  /** When provided, use this instead of running the query (dropdown is rendered by parent). */
  readonly uploadsByPlatform?: Map<string, webApi.FragmentSocialUploadItemDto[]>;
}

const notPublishedMessage = defineMessage({
  id: 'fragments.socialExport.panorama.notPublished',
  defaultMessage: 'Todavía no publicaste este fragmento en redes sociales.',
});

/**
 * Social export panorama: one BFF call for all fragment social uploads (raw media + video editor
 * projects). Shows a flat list of upload cards. When uploadsByPlatform is not provided, runs the
 * query and also shows the "Publicar en" dropdown; when provided by parent, dropdown is in the parent header.
 * In-progress uploads are polled individually via getUploadById.
 */
export function SocialExportPanorama({
  uploadsByPlatform: externalUploadsByPlatform,
}: SocialExportPanoramaProps = {}) {
  const { fragment } = useFragment();
  const { hasAvailableSources } = useAvailableSocialExportSources({
    fragmentId: fragment.id,
    stationId: fragment.stationId,
  });
  const query = useFragmentSocialUploadsQuery(
    externalUploadsByPlatform === undefined ? fragment.id : undefined,
  );
  const uploadsByPlatform = externalUploadsByPlatform ?? query.data;
  const { platforms: availablePlatforms, areConnectionsResolved } = useAvailableExportPlatforms(
    fragment.stationId,
    uploadsByPlatform,
  );
  const allUploads = Array.from(uploadsByPlatform?.values() ?? []).flat();

  const content =
    allUploads.length === 0 ? (
      !hasAvailableSources ? (
        <SocialExportNoSourcesAlert fragmentId={fragment.id} />
      ) : !areConnectionsResolved ? (
        <MutedAlert sx={{ mt: 2 }}>
          <FormattedMessage {...notPublishedMessage} />
        </MutedAlert>
      ) : availablePlatforms.length === 0 ? (
        <SocialExportNoPlatformsAlert />
      ) : (
        <MutedAlert sx={{ mt: 2 }}>
          <FormattedMessage {...notPublishedMessage} />
        </MutedAlert>
      )
    ) : (
      <Stack spacing={2}>
        {allUploads.map((upload) => (
          <SocialExportUploadItem key={upload.id} upload={upload} />
        ))}
      </Stack>
    );

  if (externalUploadsByPlatform !== undefined) {
    return content;
  }

  return (
    <QueryView query={query} inline>
      {() => content}
    </QueryView>
  );
}
